import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { PackagingFieldCrossCheckRunResults } from './PackagingFieldCrossCheckRunResults'
import {
  type PackagingFieldCrossCheckResult,
  useRunPackagingItemFieldCrossCheck,
} from '@/shared/hooks/api/packagingSuppliers'

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }
  const tagName = target.tagName
  if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
    return true
  }
  if (target.isContentEditable) {
    return true
  }
  return false
}

type PackagingItemAuditRunSheetProps = {
  supplierId: string
  itemId: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  latestEntryIsManualOverride?: boolean
}

export function PackagingItemAuditRunSheet({
  supplierId,
  itemId,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  latestEntryIsManualOverride = false,
}: PackagingItemAuditRunSheetProps) {
  const { t } = useTranslation('packagingSuppliers')
  const [internalOpen, setInternalOpen] = useState(false)
  const [replaceOverrideConfirmOpen, setReplaceOverrideConfirmOpen] =
    useState(false)
  const [latestResult, setLatestResult] =
    useState<PackagingFieldCrossCheckResult | null>(null)
  const fieldCrossCheckMutation = useRunPackagingItemFieldCrossCheck(
    supplierId,
    itemId
  )

  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const onOpenChangeRef = useRef(controlledOnOpenChange)
  onOpenChangeRef.current = controlledOnOpenChange

  const setOpen = (nextOpen: boolean) => {
    if (isControlled) {
      onOpenChangeRef.current?.(nextOpen)
    } else {
      setInternalOpen(nextOpen)
    }
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const hasModifier = event.metaKey || event.ctrlKey
      if (!hasModifier || !event.shiftKey || event.key.toLowerCase() !== 'p') {
        return
      }
      if (isEditableTarget(event.target)) {
        return
      }
      event.preventDefault()
      setOpen(true)
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setLatestResult(null)
      fieldCrossCheckMutation.reset()
    }
  }

  const handleRun = async () => {
    setLatestResult(null)
    try {
      const nextResult = await fieldCrossCheckMutation.mutateAsync()
      setLatestResult(nextResult)
    } catch {
      // isError state drives the sheet error message.
    }
  }

  const handleRunClick = () => {
    if (latestEntryIsManualOverride) {
      setReplaceOverrideConfirmOpen(true)
      return
    }
    void handleRun()
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side='right'
        className='flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl'
      >
        <SheetHeader className='shrink-0 border-b px-6 py-4 pe-10 text-start'>
          <SheetTitle>{t('itemDetail.audits.runSheetTitle')}</SheetTitle>
          <SheetDescription>
            {t('itemDetail.audits.runSheetDescription')}
          </SheetDescription>
        </SheetHeader>

        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4'>
          {fieldCrossCheckMutation.isError && (
            <p className='text-sm text-destructive'>
              {t('itemDetail.audits.runFailed')}
            </p>
          )}

          {latestResult ? (
            <PackagingFieldCrossCheckRunResults result={latestResult} />
          ) : null}
        </div>

        <SheetFooter className='shrink-0 border-t px-6 py-4 sm:flex-row sm:justify-end'>
          <Button
            disabled={fieldCrossCheckMutation.isPending}
            onClick={() => {
              handleRunClick()
            }}
          >
            {fieldCrossCheckMutation.isPending
              ? t('itemDetail.audits.running')
              : t('itemDetail.audits.runAudit')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>

      <AlertDialog
        open={replaceOverrideConfirmOpen}
        onOpenChange={setReplaceOverrideConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('itemDetail.manualOverride.replaceOverrideTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('itemDetail.manualOverride.replaceOverrideBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('itemDetail.manualOverride.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault()
                setReplaceOverrideConfirmOpen(false)
                void handleRun()
              }}
            >
              {t('itemDetail.audits.runAudit')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
